import { HEALTH_DATA_STEPS } from '@/modules/companyHealthDataConfig/entities/enums/healthData.enums';
import { cloneDeep, intersection, uniq } from 'lodash';
import { useCallback, useMemo, useState } from 'react';

export interface UseHealthDataSelectedCompanyIdsProps {
  stepHealthData: HEALTH_DATA_STEPS;
}

export const useHealthDataSelectedCompanyIds = ({
  stepHealthData,
}: UseHealthDataSelectedCompanyIdsProps) => {
  const initialState: Record<HEALTH_DATA_STEPS, string[]> = Object.values(HEALTH_DATA_STEPS).reduce(
    (acc, status) => ({ ...acc, [status]: [] }),
    {} as Record<HEALTH_DATA_STEPS, string[]>,
  );

  const [selectedCompanyIdsByStep, setSelectedCompanyIdsByStep] =
    useState<Record<HEALTH_DATA_STEPS, string[]>>(initialState);
  const [companyIdsOnPageByStep, setCompanyIdsOnPageByStep] =
    useState<Record<HEALTH_DATA_STEPS, string[]>>(initialState);

  const selectedCompanyIdsOnPage = useMemo(
    () =>
      intersection(
        companyIdsOnPageByStep[stepHealthData],
        selectedCompanyIdsByStep[stepHealthData],
      ),
    [companyIdsOnPageByStep, stepHealthData, selectedCompanyIdsByStep],
  );

  const updateCompanyIds = useCallback(
    (selected: boolean, stepHealthData: HEALTH_DATA_STEPS, companyIds: string[]) => {
      setSelectedCompanyIdsByStep((current) => {
        const clonedCurrent: string[] = uniq(cloneDeep(current[stepHealthData]));

        if (selected) {
          return {
            ...current,
            [stepHealthData]: uniq([...clonedCurrent, ...companyIds]),
          };
        }

        companyIds.forEach((companyId) => {
          const companyIdIndex = clonedCurrent.findIndex(
            (currentCompanyId) => currentCompanyId === companyId,
          );

          if (companyIdIndex > -1) {
            clonedCurrent.splice(companyIdIndex, 1);
          }
        });

        return { ...current, [stepHealthData]: clonedCurrent };
      });
    },
    [],
  );

  return {
    selectedCompanyIdsByStep,
    setSelectedCompanyIdsByStep,
    companyIdsOnPageByStep,
    setCompanyIdsOnPageByStep,
    selectedCompanyIdsOnPage,
    updateCompanyIds,
  };
};
